// Archetype Matrix — 12 archetypes + developmental matrices (AM0).
// Source of truth: docs/ARCHETYPE_MATRICES.md (cells verbatim) + docs/ARCHETYPE_MATRIX_ASSESSMENT_PLAN.md.
//
// The capstone persona-shadow assessment: Core Desire → Archetype → Trait
// Expression (10 spectra) → Integration → Light/Shadow. Developmental, never a
// fixed type. Replaces the old 6-archetype Life Archetype (…003); layers on the
// shipped Shadow Work (…013) for the trait layer.
//
// ⚠️ Matrix rows are keyed by Shadow Work spectrum id (src/data/shadowSpectra.ts)
// so a user's per-spectrum Shadow Work result maps ROW-FOR-ROW (plan §5/§6). Keep
// the labels + order aligned with shadowSpectra.ts — incl. spectrum 3 =
// "Allowing ↔ Initiative" (action_orientation).

/** Fixed assessment row id (type 'archetype'; supersedes Life Archetype …003). */
export const ARCHETYPE_ID = 'a1000000-0000-0000-0000-000000000015';

export type ArchetypeFamily = 'order' | 'freedom' | 'impact' | 'social';

/** The 5 developmental levels (matrix columns), Integrated Light → Shadow Possession. */
export const ARCHETYPE_LEVELS = [
  'Integrated Light',
  'Healthy Persona',
  'Over-Identified Persona',
  'Shadow Leakage',
  'Shadow Possession',
] as const;
export type ArchetypeLevel = (typeof ARCHETYPE_LEVELS)[number];

/** Matrix rows in display order — each is a Shadow Work spectrum id + its poles. */
export const MATRIX_ROWS: { spectrumId: string; poles: string }[] = [
  { spectrumId: 'interpersonal_approach', poles: 'Receptivity ↔ Assertiveness' },
  { spectrumId: 'emotional_expression', poles: 'Expressiveness ↔ Restraint' },
  { spectrumId: 'action_orientation', poles: 'Allowing ↔ Initiative' },
  { spectrumId: 'caregiving_style', poles: 'Nurturing ↔ Protecting' },
  { spectrumId: 'focus_orientation', poles: 'Relationship ↔ Task' },
  { spectrumId: 'cognitive_processing', poles: 'Intuition ↔ Logic' },
  { spectrumId: 'perception_perspective', poles: 'Subjectivity ↔ Objectivity' },
  { spectrumId: 'thinking_style', poles: 'Creativity ↔ Structure' },
  { spectrumId: 'ethical_reasoning', poles: 'Empathy ↔ Justice' },
  { spectrumId: 'source_of_knowledge', poles: 'Inner Wisdom ↔ External Knowledge' },
];

export const ARCHETYPE_FAMILIES: Record<ArchetypeFamily, { name: string; motive: string; color: string }> = {
  order: { name: 'Order', motive: 'structure the world', color: '#3b82f6' },
  freedom: { name: 'Freedom', motive: 'seek possibility', color: '#10b981' },
  impact: { name: 'Ego / Impact', motive: 'leave a mark', color: '#f59e0b' },
  social: { name: 'Social', motive: 'connect to others', color: '#ec4899' },
};

type MatrixRow = [string, string, string, string, string]; // L → Possession

export interface Archetype {
  id: string;
  order: number;
  family: ArchetypeFamily;
  desire: string; // desire pair, e.g. 'Order / Control'
  meaning: string; // one-line "what this archetype is for"
  lightName: string; // 'Ruler'
  shadowName: string; // 'Tyrant'
  lightQualities: string[];
  shadowQualities: string[];
  integrationGoal: string;
  color: string;
  /** 10 rows (keyed by Shadow Work spectrum id) × 5 levels. */
  matrix: Record<string, MatrixRow>;
}

export const ARCHETYPES: Archetype[] = [
  {
    id: 'ruler',
    order: 1,
    family: 'order',
    desire: 'Order / Control',
    meaning: 'To create structure and stability.',
    lightName: 'Ruler',
    shadowName: 'Tyrant',
    lightQualities: ['leads responsibly', 'creates order', 'protects systems', 'sets clear standards'],
    shadowQualities: ['dominates', 'controls', 'excludes', 'humiliates', 'weaponizes hierarchy'],
    integrationGoal: 'Lead with authority without domination.',
    color: '#3b82f6',
    matrix: {
      interpersonal_approach: ['Listens + decides', 'Clear direction', 'Must lead', 'Rejects feedback', 'Authoritarian'],
      emotional_expression: ['Presence + composure', 'Calm authority', 'Always composed', 'Emotionally distant', 'Cold intimidation'],
      action_orientation: ['Intervenes wisely', 'Takes action', 'Must manage all', 'Over-controls process', 'Micromanaging'],
      caregiving_style: ['Cares + protects', 'Protective leader', 'I know best', 'Paternalistic', 'Over-policing'],
      focus_orientation: ['People + mission', 'Builds loyalty', 'Performance first', 'Uses people as roles', 'Dehumanizing'],
      cognitive_processing: ['Vision tested by reason', 'Strategic thinker', 'My judgment is correct', 'Rigid certainty', 'Dogmatic ruler'],
      perception_perspective: ['Context + fairness', 'Applies standards', 'My standard is the standard', 'Biased enforcement', 'Detached control'],
      thinking_style: ['Preserve + improve', 'System builder', 'Order above all', 'Resists change', 'Bureaucratic rigidity'],
      ethical_reasoning: ['Justice with mercy', 'Fair leader', 'Rules must hold', 'Harsh judgment', 'Punitive tyrant'],
      source_of_knowledge: ['Inner authority + expertise', 'Informed judgment', 'I already know', 'Refuses advice', 'Absolute certainty'],
    },
  },
  {
    id: 'caregiver',
    order: 2,
    family: 'order',
    desire: 'Service / Compassion',
    meaning: 'To support, protect, and nurture.',
    lightName: 'Caregiver',
    shadowName: 'Slave / Martyr',
    lightQualities: ['nurtures', 'supports', 'protects', 'helps others grow', 'offers compassion'],
    shadowQualities: ['self-erases', 'enables dependency', 'guilt-controls', 'resents those helped'],
    integrationGoal: 'Care without losing the self.',
    color: '#3b82f6',
    matrix: {
      interpersonal_approach: ['Supports + asks', 'Attuned helper', 'Always available', 'Quiet resentment', 'Guilt-tripping'],
      emotional_expression: ['Warm + contained', 'Comforting presence', 'Always "fine"', 'Suppressed hurt', 'Martyred sighs'],
      action_orientation: ['Helps + allows', 'Steps in to help', 'Must fix everyone', 'Over-functioning', 'Smothering'],
      caregiving_style: ['Nurtures + limits', 'Devoted carer', 'I must provide', 'Enabling', 'Self-erasing rescue'],
      focus_orientation: ['Care + competence', 'People-first', 'Needs to be needed', 'Neglects own tasks', "Identity = others' needs"],
      cognitive_processing: ['Senses + checks', 'Reads needs well', 'I know your needs', 'Assumes for others', 'Imposed "help"'],
      perception_perspective: ['Empathy + perspective', 'Compassionate', 'Their pain is mine', 'Over-merging', 'Loss of self'],
      thinking_style: ['Flexible care', 'Reliable support', 'Care must look right', 'Rigid giving rituals', 'Controlling "for you"'],
      ethical_reasoning: ['Kind + fair', 'Forgiving', 'Always understanding', 'Excuses harm', 'No boundaries, used'],
      source_of_knowledge: ['Trusts + learns', 'Wise nurturer', 'I know best for you', 'Dismisses their agency', 'Infantilizing'],
    },
  },
  {
    id: 'creator',
    order: 3,
    family: 'order',
    desire: 'Innovation / Expression',
    meaning: 'To bring original ideas into form.',
    lightName: 'Creator',
    shadowName: 'Destroyer',
    lightQualities: ['builds', 'imagines', 'expresses originality', 'creates lasting value'],
    shadowQualities: ['ruins without rebuilding', 'perfectionistic', "can't finish", 'rejects all structure'],
    integrationGoal: 'Create without destroying what still has value.',
    color: '#3b82f6',
    matrix: {
      interpersonal_approach: ['Open + decisive vision', 'Shapes ideas boldly', 'My vision only', 'Dismisses input', 'Tyrannical auteur'],
      emotional_expression: ['Expressive + edited', 'Channels inspiration', 'Must express it all', 'Moody volatility', 'Creative tantrums'],
      action_orientation: ['Incubates + makes', 'Prolific maker', 'Must always create', 'Frantic output', 'Burnout churn'],
      caregiving_style: ['Tends the work', 'Grows ideas', 'The work over all', 'Neglects people', 'Sacrifices others to art'],
      focus_orientation: ['People + craft', 'Collaborative maker', 'Craft above people', 'Isolates to create', 'Lone tortured genius'],
      cognitive_processing: ['Inspiration + rigor', 'Visionary + practical', 'Trust the muse only', 'Ignores feasibility', 'Ungrounded grandiosity'],
      perception_perspective: ['Voice + standards', 'Distinct style', 'Only my taste', 'Rejects critique', 'Contempt for convention'],
      thinking_style: ['Imagine + finish', 'Brings ideas to form', 'Structure kills it', "Can't complete", 'Endless unfinished ruins'],
      ethical_reasoning: ['Cares + critiques', 'Honest maker', 'The work is truth', 'Harsh criticism', "Destroys others' work"],
      source_of_knowledge: ['Originality + learning', 'Informed originality', 'I reinvent everything', 'Rejects all precedent', 'Reinvents the broken wheel'],
    },
  },
  {
    id: 'innocent',
    order: 4,
    family: 'freedom',
    desire: 'Safety / Hope',
    meaning: 'To preserve peace, trust, and goodness.',
    lightName: 'Innocent',
    shadowName: 'Victim',
    lightQualities: ['trusts', 'hopes', 'seeks goodness', 'values simplicity', 'keeps faith'],
    shadowQualities: ['denies reality', 'avoids responsibility', 'uses helplessness', 'weaponizes naivety'],
    integrationGoal: 'Keep hope while accepting responsibility.',
    color: '#10b981',
    matrix: {
      interpersonal_approach: ['Trusts + speaks up', 'Open and hopeful', 'Avoids all conflict', 'Passive compliance', 'Helpless silence'],
      emotional_expression: ['Sincere + composed', 'Warmly open', 'Only positive feelings', 'Hidden distress', 'Collapse / overwhelm'],
      action_orientation: ['Faith + action', 'Hopeful doer', 'Waits to be saved', 'Avoids hard steps', 'Total inaction'],
      caregiving_style: ['Gentle + safe', 'Kind presence', 'Everyone is good', 'Ignores red flags', 'Repeatedly harmed'],
      focus_orientation: ['Harmony + tasks', 'Easy to be with', 'Keep everyone happy', 'Drops responsibilities', 'Dependent on rescue'],
      cognitive_processing: ['Hope + realism', 'Trusting optimist', "It'll just work out", 'Denies the facts', 'Reality-blind'],
      perception_perspective: ['Feeling + facts', 'Sees the good', 'My world is safe', 'Naive misreads', 'Willful denial'],
      thinking_style: ['Playful + steady', 'Light-hearted', 'Avoids hard structure', 'Disorganized drift', 'Chronic helplessness'],
      ethical_reasoning: ['Kind + fair', 'Forgiving soul', "No one's at fault", 'Excuses everything', 'Weaponized innocence'],
      source_of_knowledge: ['Faith + learning', 'Open learner', 'Someone will guide me', 'Avoids owning views', 'Perpetual dependence'],
    },
  },
  {
    id: 'sage',
    order: 5,
    family: 'freedom',
    desire: 'Knowledge / Truth',
    meaning: 'To understand reality clearly.',
    lightName: 'Sage',
    shadowName: 'Elitist',
    lightQualities: ['seeks truth', 'thinks deeply', 'clarifies complexity', 'illuminates'],
    shadowQualities: ['condescends', 'hides behind intellect', 'dogmatic', 'uses knowledge to exclude'],
    integrationGoal: 'Seek truth without superiority.',
    color: '#10b981',
    matrix: {
      interpersonal_approach: ['Listens + asserts truth', 'Clarifies clearly', "I'm the expert", 'Corrects everyone', 'Condescending lecture'],
      emotional_expression: ['Candid + measured', 'Thoughtful calm', 'Above emotion', 'Cold detachment', 'Bloodless intellect'],
      action_orientation: ['Reflects + acts', 'Studies then advises', 'Analysis over action', 'Endless theorizing', 'Paralysis by thought'],
      caregiving_style: ['Teaches + cares', 'Mentoring', 'Knowledge before warmth', 'Impatient with "slow"', 'Gatekeeping wisdom'],
      focus_orientation: ['People + truth', 'Wise counsel', 'Being right first', 'Dismisses feelings', 'Relationship as debate'],
      cognitive_processing: ['Insight + reason', 'Deep thinker', 'Only logic is valid', 'Overanalyzes', 'Sterile rationalism'],
      perception_perspective: ['Meaning + fact', 'Seeks truth', 'My truth is the truth', 'Dogmatic', 'Rigid orthodoxy'],
      thinking_style: ['Ideas + rigor', 'Systematic mind', 'Frameworks over life', 'Pedantic', 'Ivory-tower abstraction'],
      ethical_reasoning: ['Understanding + fairness', 'Fair-minded', 'Truth over kindness', 'Harsh "honesty"', 'Intellectual cruelty'],
      source_of_knowledge: ['Insight + evidence', 'Learned + reflective', 'I already know', 'Refuses new input', 'Closed dogmatist'],
    },
  },
  {
    id: 'explorer',
    order: 6,
    family: 'freedom',
    desire: 'Freedom / Discovery',
    meaning: 'To expand beyond limits.',
    lightName: 'Explorer',
    shadowName: 'Raider',
    lightQualities: ['seeks authenticity', 'expands horizons', 'independent', 'discovers possibility'],
    shadowQualities: ['exploits', 'escapes responsibility', 'takes without belonging', 'rejects commitment'],
    integrationGoal: 'Be free without abandoning responsibility.',
    color: '#10b981',
    matrix: {
      interpersonal_approach: ['Open + self-directed', 'Independent seeker', "Won't be tied down", 'Deflects accountability', 'Takes and leaves'],
      emotional_expression: ['Authentic + grounded', 'Free-spirited', 'Always restless', 'Detached coolness', 'Emotionally unavailable'],
      action_orientation: ['Moves + rests', 'Adventurous', 'Must keep moving', 'Avoids stillness', 'Chronic flight'],
      caregiving_style: ['Frees + protects', 'Encourages growth', "Everyone's on their own", 'Neglects dependents', 'Abandons others'],
      focus_orientation: ['Bonds + quests', 'Loyal wanderer', 'Freedom over ties', 'Drops commitments', 'Uses people for the trip'],
      cognitive_processing: ['Instinct + planning', 'Resourceful', "Rules don't apply to me", 'Reckless leaps', 'Heedless of consequence'],
      perception_perspective: ['Experience + fact', 'Curious + clear', 'My path only', "Ignores others' reality", 'Self-serving lens'],
      thinking_style: ['Improvise + anchor', 'Adaptable', 'Hates all structure', 'Flaky / unreliable', 'Rootless chaos'],
      ethical_reasoning: ['Cares + fair', 'Live-and-let-live', 'My freedom first', 'Indifferent to harm', 'Exploitative entitlement'],
      source_of_knowledge: ['Self-trust + learning', 'Learns by doing', 'I answer to no one', 'Rejects guidance', 'Lawless raider'],
    },
  },
  {
    id: 'hero',
    order: 7,
    family: 'impact',
    desire: 'Mastery / Courage',
    meaning: 'To overcome challenges and prove strength.',
    lightName: 'Hero',
    shadowName: 'Villain',
    lightQualities: ['shows courage', 'overcomes obstacles', 'protects others', 'pursues excellence'],
    shadowQualities: ['bullies', 'dominates through strength', 'must win at all costs', 'sees others as weak'],
    integrationGoal: 'Be strong without becoming oppressive.',
    color: '#f59e0b',
    matrix: {
      interpersonal_approach: ['Listens + leads charge', 'Decisive courage', 'Must win', 'Bulldozes others', 'Crushing force'],
      emotional_expression: ['Bold + composed', 'Confident calm', 'Never show weakness', 'Suppressed fear', 'Brittle bravado'],
      action_orientation: ['Acts + paces', 'Takes initiative', 'Must always push', 'Overdrives self', 'Reckless aggression'],
      caregiving_style: ['Protects + empowers', 'Defender', "I'll handle it alone", 'Overprotective', 'Domineering "savior"'],
      focus_orientation: ['Team + mission', 'Rallying leader', 'Mission over all', 'Sees people as weak', 'Sacrifices people to win'],
      cognitive_processing: ['Instinct + strategy', 'Strategic courage', 'My plan wins', 'Ignores warnings', 'Blind to defeat'],
      perception_perspective: ['Conviction + facts', 'Principled', "I'm right — fight", 'Demonizes opponents', 'All-out war'],
      thinking_style: ['Adapts + disciplines', 'Disciplined drive', 'Push through always', 'Rigid grind', 'Burnout / collateral'],
      ethical_reasoning: ['Strength + mercy', 'Just protector', 'Weakness = contempt', 'Harsh on the "weak"', 'Bullying tyrant'],
      source_of_knowledge: ['Resolve + counsel', 'Trains and learns', 'I need no one', 'Refuses help', 'Lone destructive ego'],
    },
  },
  {
    id: 'magician',
    order: 8,
    family: 'impact',
    desire: 'Power / Transformation',
    meaning: 'To transform reality through vision and influence.',
    lightName: 'Magician',
    shadowName: 'Trickster',
    lightQualities: ['transforms reality', 'catalyzes change', 'sees hidden patterns', 'influences responsibly'],
    shadowQualities: ['manipulates', 'creates illusions', 'false promises', 'power without ethics'],
    integrationGoal: 'Transform without manipulating.',
    color: '#f59e0b',
    matrix: {
      interpersonal_approach: ['Reads + acts', 'Influential', 'Bends others subtly', 'Covert pressure', 'Manipulative control'],
      emotional_expression: ['Open + measured', 'Composed presence', 'Always strategic', 'Hidden agendas', 'Deceptive mask'],
      action_orientation: ['Waits + catalyzes', 'Makes change happen', 'Must orchestrate all', 'Over-engineers', 'Compulsive scheming'],
      caregiving_style: ['Empowers + guards', 'Mentor-guide', "I'll transform you", 'Imposes "growth"', 'Coercive influence'],
      focus_orientation: ['People + vision', 'Visionary connector', 'Ends justify means', 'Uses people as pieces', 'Pure exploitation'],
      cognitive_processing: ['Vision + method', 'Pattern-seer', 'Only I see truly', 'Reads signs into all', 'Grandiose delusion'],
      perception_perspective: ['Meaning + evidence', 'Insightful', 'My reality wins', 'Reframes the facts', 'Gaslighting'],
      thinking_style: ['Vision + craft', 'Makes vision real', 'Magic over method', 'Cuts corners', 'Smoke and mirrors'],
      ethical_reasoning: ['Care + integrity', 'Ethical influence', 'Power is neutral', 'Rationalizes harm', 'Power without conscience'],
      source_of_knowledge: ['Insight + rigor', 'Deep + studied', 'I know the hidden', 'Mystifies others', 'False prophet'],
    },
  },
  {
    id: 'rebel',
    order: 9,
    family: 'impact',
    desire: 'Liberation / Change',
    meaning: 'To break restriction and renew systems.',
    lightName: 'Rebel',
    shadowName: 'Outlaw / Nihilist',
    lightQualities: ['challenges injustice', 'breaks outdated rules', 'renews systems', 'questions false authority'],
    shadowQualities: ['destroys without purpose', 'rebels for ego', 'romanticizes chaos', 'breaks trust'],
    integrationGoal: 'Challenge systems without becoming destructive.',
    color: '#f59e0b',
    matrix: {
      interpersonal_approach: ['Hears + challenges', 'Bold reformer', 'Always opposing', 'Reflexive defiance', 'Destroys all authority'],
      emotional_expression: ['Fierce + measured', 'Passionate voice', 'Must provoke', 'Simmering contempt', 'Explosive rebellion'],
      action_orientation: ['Picks battles', 'Drives change', 'Must fight everything', 'Stirs needless conflict', 'Chaos for its own sake'],
      caregiving_style: ['Frees + protects', 'Protects the wronged', 'My cause over people', 'Burns bridges', 'Harms those it "frees"'],
      focus_orientation: ['Solidarity + cause', 'Movement-builder', 'Purity over allies', 'Alienates everyone', 'Lone nihilist'],
      cognitive_processing: ['Vision + analysis', 'Sharp critic', 'My cause is truth', 'Conspiratorial', 'Ideological zealotry'],
      perception_perspective: ['Conviction + fact', 'Principled dissent', 'Only my injustice counts', 'Enemies everywhere', 'Paranoid extremism'],
      thinking_style: ['Break + rebuild', 'Renews systems', 'Destroy the system', 'Rejects all structure', 'Pure destruction'],
      ethical_reasoning: ['Compassion + justice', 'Justice-driven', 'Ends justify chaos', 'Punitive righteousness', 'Cruelty as "justice"'],
      source_of_knowledge: ['Conviction + learning', 'Questions wisely', 'I reject all norms', 'Dismisses all expertise', 'Anti-everything'],
    },
  },
  {
    id: 'lover',
    order: 10,
    family: 'social',
    desire: 'Intimacy / Beauty',
    meaning: 'To experience connection, passion, and depth.',
    lightName: 'Lover',
    shadowName: 'Seducer',
    lightQualities: ['connects deeply', 'values beauty', 'feels passion', 'builds closeness'],
    shadowQualities: ['manipulates desire', 'possessive', 'confuses love with control', 'obsessive'],
    integrationGoal: 'Desire honestly without possession.',
    color: '#ec4899',
    matrix: {
      interpersonal_approach: ['Opens + asks', 'Warm, inviting', 'Needs to be wanted', 'Clingy bids', 'Possessive demands'],
      emotional_expression: ['Passionate + grounded', 'Emotionally rich', 'All-consuming feeling', 'Jealous moods', 'Obsessive intensity'],
      action_orientation: ['Receives + pursues', 'Devoted', 'Lives for the other', 'Loses self in love', 'Total merger'],
      caregiving_style: ['Cherishes + frees', 'Tender lover', 'Love conquers all', 'Smothers', 'Controlling devotion'],
      focus_orientation: ['Closeness + life', 'Connection-first', 'Relationship is everything', 'Neglects own life', 'Identity via the other'],
      cognitive_processing: ['Heart + sense', 'Attuned', 'Feeling over reason', 'Idealizes partner', 'Blind infatuation'],
      perception_perspective: ['Feeling + perspective', 'Empathic', 'Our world only', 'Over-personalizes', 'Engulfing fusion'],
      thinking_style: ['Romance + steadiness', 'Devoted + reliable', 'Passion over commitment', 'Drama cycles', 'Unstable obsession'],
      ethical_reasoning: ['Love + honesty', 'Caring + honest', 'Love excuses all', 'Tolerates mistreatment', 'Manipulated by desire'],
      source_of_knowledge: ['Self-worth + openness', 'Secure in love', 'I need this love', 'Loses own judgment', 'Dependent on validation'],
    },
  },
  {
    id: 'jester',
    order: 11,
    family: 'social',
    desire: 'Pleasure / Laughter',
    meaning: 'To bring joy, play, and lightness.',
    lightName: 'Jester',
    shadowName: 'Hater',
    lightQualities: ['brings joy', 'uses humor wisely', 'reduces tension', 'connects through play'],
    shadowQualities: ['mocks', 'cynical', 'humiliates with humor', 'hides pain behind contempt'],
    integrationGoal: 'Tell the truth playfully without contempt.',
    color: '#ec4899',
    matrix: {
      interpersonal_approach: ['Plays + speaks truth', 'Lightens the room', 'Always "on"', 'Deflects with jokes', 'Cutting ridicule'],
      emotional_expression: ['Funny + sincere', 'Joyful presence', "Can't be serious", 'Masks real feeling', 'Bitter sarcasm'],
      action_orientation: ['Plays + rests', 'Brings levity', 'Must entertain', 'Avoids depth', 'Compulsive clowning'],
      caregiving_style: ['Heals + teases', 'Eases tension', 'Jokes over care', 'Dismisses pain', 'Mocks the hurting'],
      focus_orientation: ['Fun + presence', 'Connects via play', 'Needs to amuse', "Won't get real", 'Alienating cynicism'],
      cognitive_processing: ['Wit + insight', 'Sharp + playful', "Nothing's serious", 'Trivializes truth', 'Corrosive nihilism'],
      perception_perspective: ['Perspective + truth', 'Truth in jest', 'My joke wins', 'Pokes to wound', 'Contemptuous "honesty"'],
      thinking_style: ['Spontaneous + apt', 'Quick, clever', 'Chaos is funny', 'Undermines order', 'Tears down all value'],
      ethical_reasoning: ['Kind + honest humor', 'Humor with heart', 'Anything for a laugh', 'Punches down', 'Humiliating mockery'],
      source_of_knowledge: ['Wisdom + play', 'Wise fool', "I'm above caring", 'Cynical knowing', 'Hides pain in contempt'],
    },
  },
  {
    id: 'everyman',
    order: 12,
    family: 'social',
    desire: 'Belonging / Equality',
    meaning: 'To connect through fairness and shared humanity.',
    lightName: 'Everyman',
    shadowName: 'Conformist',
    lightQualities: ['creates belonging', 'values fairness', 'honors ordinary dignity', 'builds community'],
    shadowQualities: ['loses individuality', 'fears difference', 'blends to survive', 'obeys the group blindly'],
    integrationGoal: 'Belong without self-betrayal.',
    color: '#ec4899',
    matrix: {
      interpersonal_approach: ['Belongs + speaks up', 'Relatable, fair', "Mustn't stand out", 'Silent agreement', 'Erases own voice'],
      emotional_expression: ['Genuine + tactful', 'Easygoing', 'Keep the peace', 'Hidden true self', 'Total self-suppression'],
      action_orientation: ['Joins + initiates', 'Dependable member', 'Goes with the group', "Won't rock the boat", 'Passive conformity'],
      caregiving_style: ['Includes + steadies', 'Community-builder', 'Fit in at all costs', 'Excludes "outsiders"', 'Tribalism'],
      focus_orientation: ['Belonging + duty', 'Team player', 'Approval first', 'People-pleasing', 'Identity = the group'],
      cognitive_processing: ['Common sense + feel', 'Grounded', 'The norm is right', 'Distrusts difference', 'Herd thinking'],
      perception_perspective: ['Self + others', 'Fair-minded', '"What will they think"', 'Over-adapts', 'No self left'],
      thinking_style: ['Fits + freshens', 'Reliable', "Don't be different", 'Suppresses originality', 'Rigid sameness'],
      ethical_reasoning: ['Fairness + warmth', 'Decent + fair', 'Everyone the same', 'Resents standouts', 'Enforces conformity'],
      source_of_knowledge: ["Own view + the group's", 'Sensible', 'The group knows best', 'Defers always', 'Blind obedience'],
    },
  },
];

export const archetypeById: Record<string, Archetype> = Object.fromEntries(ARCHETYPES.map(a => [a.id, a]));

/** The non-clinical disclaimer, shown on intro + results. */
export const ARCHETYPE_DISCLAIMER =
  'This is a tool for self-reflection and growth, not a diagnosis or a fixed label. Your archetypes are patterns you can work with — they describe your current expression and your next step, never who you are for good.';

/** The guiding principle (results headline). */
export const ARCHETYPE_PRINCIPLE =
  'Desire gives the archetype. The 10 spectra give the style. Integration decides Light or Shadow — a strong lean is healthy when the opposite pole stays consciously available.';
